import { deriveEcosystem, ago } from './ecosystemState';

// Derive the command view's state from the command feed
// (GET /apps/:orgId/:appId/command on nventr.io, GET /dev/ops/ecosystem on a
// kit instance — both are the ecosystem feed plus extra blocks). Everything
// the ecosystem scene already animates comes from deriveEcosystem; this adds
// the instrument channels: cost (arc fill / comet speed), uptime ticks,
// usage skyline bars, deploy beads, finding chips, memory gauge.

const fmtUsd = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);
const SEV = ['critical', 'high', 'medium', 'low', 'info'];

export function deriveCommand(data) {
  const eco = deriveEcosystem(data);
  const d = data || {};
  const c = d.costs?.current || d.costs || null; // nventr.io: { current, prior }; kit: flat
  const hasCost = !!c && Number.isFinite(Number(c.mtdUsd));
  const limitUsd = Number(d.limits?.app) || null;
  const mtd = hasCost ? Number(c.mtdUsd) || 0 : 0;
  const projected = hasCost ? Number(c.projectedUsd) || 0 : 0;
  const burn = hasCost ? Number(c.burnPerDayUsd) || 0 : 0;
  // Arc fill: against the limit when one exists, else against the projection
  // (so the arc always means "how far through what this month will cost").
  const denom = limitUsd || projected || mtd || 1;
  const cost = {
    available: hasCost,
    mtdUsd: hasCost ? mtd : null,
    burnPerDayUsd: hasCost ? burn : null,
    projectedUsd: hasCost ? projected : null,
    priorUsd: d.costs?.prior?.mtdUsd ?? null,
    priorPeriod: d.costs?.priorPeriod || null,
    limitUsd,
    ratio: hasCost ? mtd / denom : 0,
    ghost: hasCost ? projected / denom : null,
    ticks: limitUsd ? [0.5, 0.8, 1] : [],
    level: !hasCost ? 'off' : limitUsd && mtd >= limitUsd ? 'down' : limitUsd && (mtd >= limitUsd * 0.8 || projected > limitUsd) ? 'warn' : 'ok',
    // Comet: one lap per (24h / burn-normalised) — map $0..$20/day → 0..0.35 laps/s.
    cometSpeed: hasCost && burn > 0 ? Math.min(0.35, 0.03 + burn / 60) : 0,
    infra: c?.infra || null,
    model: c?.model || null,
    dev: c?.dev || null,
    byUser: c?.byUser || {},
    pulledAt: c?.infra?.pulledAt || c?.pulledAt || null,
    text: hasCost ? `${fmtUsd(mtd)} mtd · ${fmtUsd(burn)}/day · ${fmtUsd(projected)} projected${limitUsd ? ` · limit ${fmtUsd(limitUsd)}` : ''}` : '',
  };

  const h = d.health || {};
  const health = {
    days: h.days || [],
    ticks: (h.days || []).map((x) => x.uptime),
    uptime14d: h.uptime14d ?? null,
    p50: h.p50 ?? null,
    p95: h.p95 ?? null,
    features: h.features || { total: 0, passing: 0, failing: [] },
    // Wobble amplitude for the monitor ring: 0 at ≤200ms, 1 at ≥2s p95.
    wobble: h.p95 != null ? Math.min(1, Math.max(0, (h.p95 - 200) / 1800)) : 0,
    incidentsOpen: (d.incidents || d.plane?.incidents || []).filter((i) => i.status === 'open').length,
  };

  const u = d.usage || null;
  const counters = u?.counters || {};
  const USAGE_KEYS = [
    ['tokens', 'tokens'], ['aiTurns', 'turns'], ['pipelineRuns', 'runs'], ['jobRuns', 'jobs'],
    ['agentConversations', 'convos'], ['devCents', 'dev ¢'], ['voiceMinutes', 'voice'],
  ];
  const topUsers = u?.byUser || [];
  const totalTok = Number(counters.tokens) || 0;
  const usage = {
    available: !!u,
    counters,
    bars: USAGE_KEYS.map(([k, label]) => ({
      key: k, label, value: Number(counters[k]) || 0,
      segments: k === 'tokens' && totalTok ? topUsers.slice(0, 3).map((x) => x.tokens / totalTok) : [],
    })).filter((b) => b.value > 0 || ['tokens', 'aiTurns', 'pipelineRuns'].includes(b.key)),
    topUsers,
    reportedAt: u?.reportedAt || null,
    lastActivityAt: u?.lastActivityAt || null,
    statement: d.statement || null,
  };

  const deploys = (d.recentDeploys || d.deploys || []).map((x) => ({
    env: x.env === 'uat' ? 'uat' : 'prod', status: x.status, at: x.at, sha: x.headSha || x.mergeSha || null,
    level: x.status === 'error' ? 'down' : x.status === 'done' ? 'ok' : 'warn',
  }));
  const beads = {
    prod: deploys.filter((x) => x.env === 'prod').slice(0, 5).reverse(),
    uat: deploys.filter((x) => x.env === 'uat').slice(0, 5).reverse(),
  };

  const counts = eco.security.counts || {};
  const security = {
    ...eco.security,
    bySeverity: SEV.map((k) => ({ key: k, count: Number(counts[k]) || 0 })),
    nextBaselineAt: d.scanSchedule?.nextBaselineAt || null,
    assurance: !!(d.instance || d.plane?.instance)?.assurance,
  };

  const host = d.host || null;
  const memory = {
    available: !!host?.load,
    memMb: host?.load?.memMb ?? null,
    cpuPct: host?.load?.cpuPct ?? null,
    share: host?.memShare ?? null,
    hostId: host?.id || null,
    hostVitals: host?.vitals || null,
  };

  const lic = d.license || null;
  const inst = d.instance || d.plane?.instance || {}; // kit feed nests it under plane
  const identity = {
    name: inst.name || eco.core.name,
    status: inst.status || eco.core.status,
    plan: d.plan || inst.plan || null,
    url: inst.url || d.plane?.instance?.url || null,
    kitRef: inst.kitRef || null,
    version: inst.version || null,
    pinnedKitRef: d.pinnedKitRef || null,
    kitDrift: !!(inst.kitRef && d.pinnedKitRef && inst.kitRef !== d.pinnedKitRef),
    prepaidUntil: d.prepaidUntil || null,
    license: lic ? { status: lic.status || lic.state, lastSeenAt: lic.lastSeenAt || null, termEnd: lic.termEnd || lic.graceUntil || null } : (lic === null && d.license !== undefined ? null : eco.plane.license ? { status: eco.plane.license } : null),
    period: d.period || null,
    assurance: !!inst.assurance,
  };

  // Raw records the stage cards read (see commandInspector): the scene
  // animates levels, the cards need the words behind them.
  const planeSrc = d.plane && !d.plane.unreachable ? d.plane : d;
  const incidents = (planeSrc?.incidents || d.incidents || []).map((i) => ({ id: i.id, kind: i.kind || null, status: i.status, reason: i.reason || '', openedAt: i.openedAt || 0, closedAt: i.closedAt || null }));
  const servicesAt = d.servicesAt || planeSrc?.servicesAt || null;
  const provisioning = planeSrc?.provisioning || d.provisioning || null;
  const updates = planeSrc?.updates || {};
  const twinSrc = planeSrc?.twin || null;
  const prodSrc = planeSrc?.prod || null;

  return { ...eco, cost, health, usage, beads, security, memory, identity, deploys, incidents, servicesAt, provisioning, updates: { versionBehind: !!updates.versionBehind, latestKitVersion: updates.latestKitVersion || null, planeSha: updates.planeSha || null }, twinSrc, prodSrc };
}

// ---------------------------------------------------------------------------
// Topology. `deriveTopology(state, telemetry)` turns the command state plus
// the normalised telemetry into the request-path model TopologyScene draws:
// edge → ingress conduit → app chassis (rack, memory, exceptions, shield)
// → work lanes / control plane / peers, a UAT lane, and the floor timeline.
// Every field here is a measurement the scene binds a shape or a motion to;
// nothing is decorative. Sim scenarios that cannot come from traffic rows
// (a container down, a deploy running, TLS expiring, a scan running) are
// overlaid here from `telemetry.scenario` so the stage still has one model.

const SEV_LEVEL = { critical: 'down', high: 'down', medium: 'warn', low: 'ok', info: 'ok' };
const TLS_FULL_DAYS = 90;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const lvlWorst = (...ls) => (ls.includes('down') ? 'down' : ls.includes('warn') ? 'warn' : ls.includes('busy') ? 'busy' : ls.includes('ok') ? 'ok' : 'off');

function simOverlay(s, t) {
  if (t?.source !== 'sim' || !t.scenario) return s;
  const scn = t.scenario;
  if (scn === 'service-down') {
    const services = s.services.map((x, i) => (i === 0 && x.kind !== 'runner' ? { ...x, level: 'down', status: 'exited (137) 2 minutes ago', exitCode: 137 } : i === 1 ? { ...x, level: 'warn', status: 'restarting', createdAt: Date.now() - 90e3 } : x));
    return { ...s, services: services.length ? services : [{ name: 'backend', kind: 'service', level: 'down', status: 'exited (137) 2 minutes ago' }, { name: 'worker', kind: 'service', level: 'warn', status: 'restarting', createdAt: Date.now() - 90e3 }], servicesAt: Date.now() };
  }
  if (scn === 'deploy-running') return { ...s, twin: { ...s.twin, exists: true, deployRunning: 'uat', level: s.twin.exists ? s.twin.level : 'ok' } };
  if (scn === 'tls-expiring') {
    const edge = s.edge.length ? s.edge.map((d, i) => (i === 0 ? { ...d, tlsDaysLeft: 5, level: 'down' } : d)) : [{ host: `${s.viewer?.slug || 'app'}.apps.nventr.io`, label: 'app', tlsDaysLeft: 5, level: 'down' }];
    return { ...s, edge };
  }
  if (scn === 'scan-running') return { ...s, security: { ...s.security, scanning: true, level: 'warn' } };
  return s;
}

export function deriveTopology(state0, t) {
  const s = simOverlay(state0, t);
  const slug = s.viewer?.slug || s.core.name || null;
  const twinSlug = slug ? `${slug}-uat` : null;
  const now = t?.window?.now || Date.now();
  const live = (t?.live && slug && t.live[slug]) || null;
  const liveUat = (t?.live && twinSlug && t.live[twinSlug]) || null;
  const ser = (t?.series && slug && t.series[slug]) || null;
  const rt = (t?.runtime && slug && t.runtime[slug]) || null;
  const rtFresh = !!rt && !(rt.at && now - rt.at > 3 * 3600e3);
  const openAnoms = (t?.anomalies || []).filter((a) => a.closedAt == null && (a.slug === slug || a.slug === twinSlug || a.to === slug));

  // Edge: gates with a TLS arc each; audience counters.
  const lastRow = ser?.ts?.length ? ser.ts.length - 1 : -1;
  const uniqIps = lastRow >= 0 && ser.uniqIps ? num(ser.uniqIps[lastRow]) : null;
  const domains = s.edge.map((d) => ({ ...d, tlsRatio: d.tlsDaysLeft == null ? null : Math.max(0, Math.min(1, d.tlsDaysLeft / TLS_FULL_DAYS)) }));
  const tlsMin = domains.reduce((m, x) => (x.tlsDaysLeft != null ? (m == null ? x.tlsDaysLeft : Math.min(m, x.tlsDaysLeft)) : m), null);
  const edge = {
    domains, level: domains.length ? lvlWorst(...domains.map((d) => d.level)) : 'off',
    uniqIps, activeUsers30m: rtFresh ? num(rt.activeUsers30m) : null, activeUsersToday: rtFresh ? num(rt.activeUsersToday) : null,
    word: domains.length ? `${domains.length} domain${domains.length === 1 ? '' : 's'}${tlsMin != null ? ` · TLS ${tlsMin}d` : ''}` : 'no domains',
  };

  // Ingress conduit: packets at the real rate, coloured by the real mix.
  // The newest bucket is still filling (the instance flushes every few
  // minutes), so "now" is the newest of the last two buckets that has data.
  const cur = lastRow >= 0 && !num(ser.req[lastRow]) && lastRow > 0 && num(ser.req[lastRow - 1]) ? lastRow - 1 : lastRow;
  const at = (k) => (cur >= 0 && ser[k] ? num(ser[k][cur]) : 0);
  const req = ser ? at('req') : live ? num(live.req) : 0;
  const s5 = ser ? at('s5xx') : live ? num(live.s5xx) : 0;
  const s4 = at('s4xx');
  const p95 = ser ? at('p95') : live ? num(live.p95) : 0;
  const p50 = at('p50');
  // 1 packet per `density` requests, chosen so the busiest case shows ~10/s.
  const density = req > 600 ? Math.ceil(req / 600) : 1;
  const rate = req > 0 ? Math.max(0.4, Math.min(10, req / 60 / density)) : 0;
  const errRate = req > 0 ? s5 / req : 0;
  const warnRate = req > 0 ? s4 / req : 0;
  const mixDown = Math.min(20, Math.round(20 * errRate)); const mixWarn = Math.min(20 - mixDown, Math.round(20 * warnRate));
  const ingress = {
    reqPerMin: req, s5xxPerMin: s5, s4xxPerMin: s4, p50, p95, errRate, density, rate,
    mix: { ok: 20 - mixDown - mixWarn, warn: mixWarn, down: mixDown },
    jitter: p95 > 200 ? Math.min(1, (p95 - 200) / 1800) : 0,
    speed: p50 > 0 ? Math.max(0.35, Math.min(1.2, 300 / p50)) : 0.8,
    level: req <= 0 ? 'off' : errRate >= 0.05 ? 'down' : errRate >= 0.01 || p95 > 1500 ? 'warn' : 'ok',
    word: req <= 0 ? 'no traffic' : `${fmtN(req)}/min · ${s5 > 0 ? `${fmtN(s5)} 5xx` : 'no 5xx'} · p95 ${fmtMs(p95)}`,
  };

  // App chassis: rack of services, memory fill, exceptions, users.
  const services = s.services.map((x) => ({ ...x, ageMs: x.createdAt ? Math.max(0, now - new Date(x.createdAt).getTime()) : null }));
  const svcOnly = services.filter((x) => x.kind !== 'runner');
  const up = svcOnly.filter((x) => x.level === 'ok').length;
  const appLevel = lvlWorst(s.core.level, ...svcOnly.map((x) => (x.level === 'off' ? 'ok' : x.level)));
  const exceptions = rtFresh ? { lastHour: num(rt.exceptionsLastHour), list: rt.exceptions || [] } : { lastHour: 0, list: [] };
  const memRatio = s.memory.available ? (s.memory.share != null ? s.memory.share : Math.min(1, num(s.memory.memMb) / 2048)) : null;
  const app = {
    level: appLevel, status: s.identity.status || s.core.status || '', name: s.identity.name || slug || 'instance',
    services, up, total: svcOnly.length,
    memory: { ...s.memory, ratio: memRatio, level: memRatio == null ? 'off' : memRatio > 0.85 ? 'down' : memRatio > 0.6 ? 'warn' : 'ok' },
    exceptions, kitRef: s.identity.kitRef, version: s.identity.version, kitDrift: s.identity.kitDrift, pinnedKitRef: s.identity.pinnedKitRef,
    word: `${s.identity.status || 'live'}${svcOnly.length ? ` · ${up}/${svcOnly.length} up` : ''}${exceptions.lastHour ? ` · ${exceptions.lastHour} exc/h` : ''}`,
  };

  // Shield: findings as chips by severity; a scan in progress sweeps.
  const sev = s.security.bySeverity || [];
  const chips = sev.filter((x) => x.count > 0).map((x) => ({ sev: x.key, count: x.count, level: SEV_LEVEL[x.key] || 'ok' }));
  const security = {
    ...s.security, chips, total: chips.reduce((a, x) => a + x.count, 0),
    word: s.security.scanning ? 'scanning' : s.security.lastScanAt ? `${s.security.counts.critical || 0}c ${s.security.counts.high || 0}h` : 'never scanned',
  };

  // Work lanes: one per queue the instance reports.
  const lanes = rtFresh ? Object.entries(rt.queues || {}).map(([id, q]) => {
    const stale = num(q.stale); const queued = num(q.queued); const running = num(q.running); const oldest = num(q.oldestQueuedMs);
    const level = stale > 0 ? 'down' : queued > 0 && oldest > 30 * 60e3 ? 'warn' : running > 0 ? 'busy' : queued > 0 ? 'ok' : 'off';
    return { id, label: q.label || id, queued, running, stale, oldestQueuedMs: oldest, level };
  }).sort((a, b) => (b.stale - a.stale) || (b.queued - a.queued) || (b.running - a.running)) : [];
  const totals = rtFresh ? (rt.totals || { queued: 0, running: 0, stale: 0 }) : { queued: 0, running: 0, stale: 0 };
  const work = {
    lanes, totals, at: rt?.at || null, level: lanes.length ? lvlWorst(...lanes.map((l) => l.level)) : 'off',
    word: !rtFresh ? 'no runtime report' : lanes.length ? `${num(totals.queued)} queued · ${num(totals.running)} running${num(totals.stale) ? ` · ${num(totals.stale)} stale` : ''}` : 'no queues',
  };

  // Control plane: calls at the real rate, the probe as a visible round trip.
  const pc = (t?.planeCalls && slug && t.planeCalls[slug]) || null;
  const licState = s.identity.license?.status || s.plane.license || null;
  const latch = !licState || licState === 'dormant' || licState === 'pending' ? 'off' : ['valid', 'active'].includes(licState) ? 'open' : licState === 'grace' ? 'half' : 'barred';
  const plane = {
    ...s.plane, callsPerMin: pc ? num(pc.ratePerMin) : 0, lastCallAt: pc?.lastAt || null,
    license: { state: licState, latch }, kitDrift: s.identity.kitDrift, pinnedKitRef: s.identity.pinnedKitRef, kitRef: s.identity.kitRef,
    probe: { lastCheckAt: s.monitor.lastCheckAt || 0, latencyMs: s.monitor.latencyMs, level: s.monitor.level },
    word: !s.plane.reachable ? 'unreachable' : `${licState || 'licensed'}${s.updates.versionBehind ? ' · update' : ''}${s.identity.kitDrift ? ' · drift' : ''}`,
  };

  // UAT lane.
  const uatReq = liveUat ? num(liveUat.req) : 0;
  const uat = {
    exists: !!s.twin.exists, level: s.twin.level, status: s.twinSrc?.status || null, slug: twinSlug,
    deployRunning: s.twin.deployRunning, reqPerMin: uatReq,
    rate: uatReq > 0 ? Math.max(0.3, Math.min(6, uatReq / 60)) : 0, errRate: uatReq > 0 ? num(liveUat.s5xx) / uatReq : 0,
    word: !s.twin.exists ? 'no uat twin' : s.twin.deployRunning === 'uat' ? 'deploying' : `${s.twinSrc?.status || 'live'}${uatReq ? ` · ${fmtN(uatReq)}/min` : ' · quiet'}`,
  };
  const deploys = { prod: s.beads.prod, uat: s.beads.uat, running: s.twin.deployRunning };

  // Peers: live inter-app flows touching this app.
  const peers = (t?.flows || []).filter((f) => (f.from === slug || f.to === slug) && f.to !== twinSlug && f.from !== twinSlug).map((f) => ({
    id: `${f.from}>${f.to}`, slug: f.from === slug ? f.to : f.from, dir: f.from === slug ? 'out' : 'in', rate: num(f.ratePerMin), kind: f.kind,
  })).slice(0, 6);

  // Floor timeline: one time axis for traffic, deploys, incidents, anomalies.
  const windowMs = t?.window?.ms || 86400e3;
  const t0 = now - windowMs;
  const u = (at) => (at ? Math.max(0, Math.min(1, (at - t0) / windowMs)) : null);
  let buckets = [];
  if (ser?.ts?.length) {
    const n = ser.ts.length; const target = 48; const per = Math.max(1, Math.ceil(n / target));
    for (let i = 0; i < n; i += per) {
      let r = 0; let e = 0;
      for (let k = i; k < Math.min(n, i + per); k += 1) { r += num(ser.req[k]); e += num(ser.s5xx[k]); }
      buckets.push({ ts: ser.ts[i], req: r / Math.min(per, n - i), s5xx: e });
    }
  }
  const timeline = {
    windowLabel: t?.window?.label || '24h', buckets,
    deploys: s.deploys.filter((d) => d.at && d.at >= t0).map((d) => ({ u: u(d.at), level: d.level, env: d.env, sha: d.sha, at: d.at })),
    marks: [
      ...s.incidents.filter((i) => i.openedAt >= t0).map((i) => ({ u: u(i.openedAt), level: i.status === 'open' ? (i.kind === 'anomaly' ? 'warn' : 'down') : 'ok', kind: i.kind || 'incident', at: i.openedAt, open: i.status === 'open' })),
      ...(t?.anomalies || []).filter((a) => a.slug === slug && a.openedAt >= t0).map((a) => ({ u: u(a.openedAt), level: a.closedAt ? 'ok' : (a.severity === 'down' || a.severity === 'high' || a.severity === 'critical' ? 'down' : 'warn'), kind: `${a.metric} ${a.kind}`, at: a.openedAt, open: a.closedAt == null, id: a.id })),
    ].sort((a, b) => a.at - b.at).slice(-12),
    uptime: (s.health.days || []).slice(-14).map((d) => d.uptime),
    uptime14d: s.health.uptime14d,
  };

  // Where each open anomaly shows up on the stage. One flag per place +
  // word (two spike rules firing on the same metric read as one event).
  const flagged = new Map();
  const anomalies0 = openAnoms.map((a) => ({
    id: a.id, severity: a.severity === 'down' || a.severity === 'high' || a.severity === 'critical' ? 'down' : 'warn',
    place: a.slug === twinSlug ? 'uat' : a.metric === 'queue' ? 'work' : a.metric === 'tokens' || a.metric === 'cost' ? 'cost' : a.kind === 'cascade' || a.to === slug ? 'peer' : 'ingress',
    word: a.kind === 'cascade' ? 'cascade' : a.kind === 'stuck' ? 'worker stuck' : a.kind === 'backlog' ? 'queue backlog' : a.metric === 'p95' ? 'latency' : `${a.metric === 'req' ? 'traffic' : a.metric} ${a.kind}`.trim(),
    peer: a.from || null,
  }));
  for (const a of anomalies0) {
    const k = `${a.place}:${a.peer || ''}:${a.word}`;
    const cur = flagged.get(k);
    if (!cur) flagged.set(k, { ...a, more: 0 });
    else { cur.more += 1; if (a.severity === 'down') cur.severity = 'down'; }
  }
  const anomalies = [...flagged.values()];

  const overall = lvlWorst(s.overall, ingress.level === 'off' ? 'ok' : ingress.level, work.level === 'down' || work.level === 'warn' ? work.level : 'ok');
  return { slug, twinSlug, overall, edge, ingress, app, security, work, plane, uat, deploys, peers, timeline, cost: s.cost, anomalies, now };
}

// The strip above the stage: one chip per column, in request order, each
// carrying the column's level and its one-line word. Passive — it is the
// legend of the path, not a filter.
export function topologyColumns(state, t) {
  if (!state) return [];
  const g = deriveTopology(state, t);
  const lvl = (l) => (l === 'busy' ? 'busy' : l === 'off' ? 'idle' : l);
  return [
    { key: 'edge', label: 'edge', level: lvl(g.edge.level), word: g.edge.word },
    { key: 'ingress', label: 'ingress', level: lvl(g.ingress.level), word: g.ingress.word },
    { key: 'production', label: 'production', level: lvl(g.app.level), word: g.app.word },
    { key: 'work', label: 'work', level: lvl(g.work.level), word: g.work.word },
    { key: 'plane', label: 'control plane', level: lvl(g.plane.reachable ? g.plane.level : 'down'), word: g.plane.word },
    ...(g.uat.exists ? [{ key: 'uat', label: 'uat', level: lvl(g.uat.deployRunning === 'uat' ? 'busy' : g.uat.level), word: g.uat.word }] : []),
    ...(g.peers.length ? [{ key: 'peers', label: 'peers', level: g.anomalies.some((a) => a.place === 'peer') ? 'warn' : 'ok', word: `${new Set(g.peers.map((p) => p.slug)).size} live` }] : []),
  ];
}

// ---------------------------------------------------------------------------
// Stage cards. `commandInspector(state, telemetry, sel)` builds the DOM card
// for a selection descriptor `{ kind, id }` emitted by CommandScene /
// EcosystemScene — rebuilt from the CURRENT state on every render so a
// pinned card is live. Kinds: core · service (name) · monitor · security ·
// twin (prod|uat) · edge (host) · plane · cost · usage · memory · flow
// (from>to) · anomaly (id). `sel == null` → the core card (default).
// Card shape matches CommandView: { kind, id, title, subtitle, tone, rows,
// sparks, sections, issues, open }.

const fmtN = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v) >= 1e6 ? `${(Number(v) / 1e6).toFixed(1)}M` : Number(v) >= 1e3 ? `${(Number(v) / 1e3).toFixed(1)}k` : String(Math.round(Number(v))));
const fmtMs = (v) => (v == null ? '—' : `${Math.round(v)} ms`);
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(v >= 0.999 ? 2 : 1)}%`);
const shaOf = (s) => (s ? String(s).slice(0, 7) : '—');
const toneOf = (level) => (level === 'warn' ? 'warn' : level === 'down' ? 'down' : level === 'ok' ? 'ok' : 'off');
const last = (arr) => (Array.isArray(arr) && arr.length ? arr[arr.length - 1] : null);

function trafficSparks(t, slug, { tokens = false } = {}) {
  const s = t?.series?.[slug];
  if (!s?.req?.length) return [];
  const out = [
    { label: 'requests / min', values: s.req.slice(-24), color: 'ok', last: `${fmtN(last(s.req))}/min` },
    { label: '5xx', values: (s.s5xx || []).slice(-24), color: (last(s.s5xx) || 0) > 0 ? 'down' : 'line', last: fmtN(last(s.s5xx) || 0) },
    { label: 'p95 latency', values: (s.p95 || []).slice(-24), color: (last(s.p95) || 0) > 1500 ? 'warn' : 'line', last: fmtMs(last(s.p95)) },
  ];
  if (tokens && s.tokens?.some((v) => v > 0)) out.push({ label: 'tokens', values: s.tokens.slice(-24), color: 'ok', last: fmtN(last(s.tokens)) });
  return out;
}

function flowSections(t, slug) {
  const flows = t?.flows || [];
  const out = flows.filter((f) => f.from === slug); const inn = flows.filter((f) => f.to === slug);
  const secs = [];
  if (out.length) secs.push({ title: 'flows out', rows: out.map((f) => [`→ ${f.to}`, `${fmtN(f.ratePerMin)}/min · ${f.kind}`]) });
  if (inn.length) secs.push({ title: 'flows in', rows: inn.map((f) => [`← ${f.from}`, `${fmtN(f.ratePerMin)}/min · ${f.kind}`]) });
  const rt = t?.runtime?.[slug];
  if (rt?.totals) secs.push({ title: 'runtime', rows: [['queued', fmtN(rt.totals.queued)], ['running', fmtN(rt.totals.running)], ['stale', fmtN(rt.totals.stale)]] });
  const an = (t?.anomalies || []).filter((a) => a.slug === slug && a.closedAt == null);
  if (an.length) secs.push({ title: `open anomalies · ${an.length}`, dots: an.slice(0, 5).map((a) => ({ level: a.severity === 'high' || a.severity === 'critical' ? 'down' : 'warn', text: `${a.metric} ${a.kind} · z ${Number(a.z).toFixed(1)}` })) });
  return secs;
}

function coreIssues(s) {
  const issues = [];
  const open = s.incidents.filter((i) => i.status === 'open');
  if (s.identity.status && s.identity.status !== 'live' && s.identity.status !== 'running') issues.push({ kind: 'status', level: s.identity.status === 'provisioning' ? 'busy' : 'down', text: `instance ${s.identity.status}${s.provisioning?.stage && s.provisioning.status !== 'done' ? ` · ${s.provisioning.stage}` : ''}` });
  if (['blocked', 'suspended', 'revoked'].includes(s.identity.license?.status)) issues.push({ kind: 'license', level: 'down', text: `license ${s.identity.license.status}` });
  else if (s.identity.license?.status === 'grace') issues.push({ kind: 'license', level: 'warn', text: 'license in grace' });
  if (!s.plane.reachable && s.plane.level !== 'off') issues.push({ kind: 'plane', level: 'warn', text: 'control plane unreachable' });
  if (open.length) issues.push({ kind: 'incident', level: open.some((i) => i.kind !== 'anomaly') ? 'down' : 'warn', text: `${open.length} open incident${open.length === 1 ? '' : 's'} · ${open[0].reason || open[0].kind || 'probe failed'}` });
  else if (s.monitor.level === 'warn') issues.push({ kind: 'probe', level: 'warn', text: 'monitor: probe failing' });
  else if (s.monitor.level === 'down') issues.push({ kind: 'probe', level: 'down', text: 'monitor: down' });
  const down = s.services.filter((x) => x.kind === 'service' && x.level === 'down');
  if (down.length) issues.push({ kind: 'service', level: 'down', text: `${down.length} service${down.length > 1 ? 's' : ''} down · ${down.map((x) => x.name).slice(0, 3).join(', ')}` });
  if (s.twin.deployRunning) issues.push({ kind: 'deploy', level: 'busy', text: `deploy running → ${s.twin.deployRunning}` });
  if (s.security.scanning) issues.push({ kind: 'scanning', level: 'busy', text: 'security scan running' });
  else if ((s.security.counts.critical || 0) + (s.security.counts.high || 0) > 0) issues.push({ kind: 'scan', level: s.security.counts.critical ? 'down' : 'warn', text: `scan · ${s.security.counts.critical || 0} critical · ${s.security.counts.high || 0} high` });
  const tlsMin = s.edge.reduce((m, x) => (x.tlsDaysLeft != null ? Math.min(m, x.tlsDaysLeft) : m), 99);
  if (tlsMin < 14) issues.push({ kind: 'tls', level: tlsMin < 3 ? 'down' : 'warn', text: `TLS ${tlsMin}d left` });
  if (s.updates.versionBehind) issues.push({ kind: 'version', level: 'warn', text: `kit update available${s.updates.latestKitVersion ? ` · ${s.updates.latestKitVersion}` : ''}` });
  if (s.identity.kitDrift) issues.push({ kind: 'drift', level: 'warn', text: `kit ${s.identity.kitRef} · pinned ${s.identity.pinnedKitRef}` });
  if (s.cost.level === 'warn' || s.cost.level === 'down') issues.push({ kind: 'cost', level: s.cost.level, text: s.cost.level === 'down' ? 'over the monthly cost limit' : 'projected over the cost limit' });
  if (s.health.features.failing.length) issues.push({ kind: 'feature', level: 'warn', text: `${s.health.features.failing.length} feature check${s.health.features.failing.length > 1 ? 's' : ''} failing · ${s.health.features.failing.slice(0, 2).join(', ')}` });
  return issues;
}

function coreCard(s, t) {
  const slug = s.viewer?.slug || s.core.name;
  const live = t?.live?.[slug];
  const pc = t?.planeCalls?.[slug];
  const id = s.identity;
  const issues = coreIssues(s);
  const rows = [
    ['status', `${id.status || '—'}${s.monitor.level !== 'off' ? ` · monitor ${s.monitor.level}` : ''}`],
    ['probe', s.monitor.lastCheckAt ? `${fmtMs(s.monitor.latencyMs)} · ${ago(s.monitor.lastCheckAt)}` : 'no probe yet'],
    ['uptime 14d', `${pct(s.health.uptime14d)} · p95 ${fmtMs(s.health.p95)}`],
    live ? ['traffic now', `${fmtN(live.req)}/min · ${fmtN(live.s5xx)} 5xx · p95 ${fmtMs(live.p95)}`] : null,
    pc ? ['plane calls', `${fmtN(pc.ratePerMin)}/min`] : null,
    s.cost.available ? ['cost mtd / proj', `${fmtUsd(s.cost.mtdUsd)} / ${fmtUsd(s.cost.projectedUsd)}${s.cost.limitUsd ? ` · limit ${fmtUsd(s.cost.limitUsd)}` : ''}`] : null,
    s.cost.available ? ['burn / day', fmtUsd(s.cost.burnPerDayUsd)] : null,
    s.usage.available ? ['usage', `${fmtN(s.usage.counters.tokens)} tokens · ${fmtN(s.usage.counters.aiTurns)} turns · ${fmtN(s.usage.counters.pipelineRuns)} runs`] : null,
    ['services', s.services.length ? `${s.services.filter((x) => x.level === 'ok').length}/${s.services.length} up${s.servicesAt ? ` · ${ago(s.servicesAt)}` : ''}` : 'no report'],
    ['kit', `${id.kitRef || '—'}${id.version ? ` · v${id.version}` : ''}${id.kitDrift ? ` · pinned ${id.pinnedKitRef}` : ''}`],
    id.license ? ['license', `${id.license.status}${id.license.lastSeenAt ? ` · seen ${ago(id.license.lastSeenAt)}` : ''}`] : null,
    s.usage.lastActivityAt ? ['last activity', ago(s.usage.lastActivityAt)] : null,
    id.url ? ['url', id.url.replace(/^https?:\/\//, '')] : null,
  ].filter(Boolean);
  const sections = [];
  if (s.services.length) sections.push({ title: `services · ${s.services.filter((x) => x.level === 'ok').length}/${s.services.length} up`, dots: s.services.slice(0, 12).map((x) => ({ level: x.level === 'off' ? 'idle' : x.level, text: `${x.name}${x.status && x.status !== 'running' ? ` · ${x.status}` : ''}` })) });
  const open = s.incidents.filter((i) => i.status === 'open');
  if (open.length) sections.push({ title: `open incidents · ${open.length}`, dots: open.slice(0, 5).map((i) => ({ level: i.kind === 'anomaly' ? 'warn' : 'down', text: `${i.reason || i.kind || 'probe failed'} · ${ago(i.openedAt)}` })) });
  sections.push(...flowSections(t, slug));
  if (s.provisioning && s.provisioning.status !== 'done') sections.push({ title: 'provisioning', rows: [['job', `${s.provisioning.kind || 'provision'} · ${s.provisioning.status}`], ['stage', s.provisioning.stage || '—'], ['at', ago(s.provisioning.at)]] });
  return {
    kind: 'core', id: 'core', title: id.name || slug || 'instance', tone: toneOf(s.overall),
    subtitle: [slug, id.plan, s.viewer?.role === 'uat' ? 'you are on the UAT twin' : null].filter(Boolean).join(' · '),
    rows, sparks: trafficSparks(t, slug, { tokens: true }), sections, issues, open: 'ap-identity',
  };
}

function serviceCard(s, name) {
  const x = s.services.find((v) => v.name === name);
  if (!x) return null;
  return {
    kind: 'service', id: name, title: name, subtitle: x.kind === 'runner' ? 'runner · finishes and exits' : 'container', tone: x.level === 'off' ? 'idle' : toneOf(x.level),
    rows: [['state', x.status || '—'], ['health', x.level === 'ok' ? 'healthy' : x.level === 'warn' ? 'starting' : x.level === 'down' ? 'down' : 'stopped'], x.exitCode != null && x.exitCode !== 0 ? ['exit code', String(x.exitCode)] : null, x.createdAt ? ['container started', ago(new Date(x.createdAt).getTime())] : null, x.image ? ['image', x.image] : null, s.servicesAt ? ['reported', ago(s.servicesAt)] : null].filter(Boolean),
    sections: [{ title: `all services · ${s.services.filter((v) => v.level === 'ok').length}/${s.services.length} up`, dots: s.services.slice(0, 12).map((v) => ({ level: v.level === 'off' ? 'idle' : v.level, text: v.name })) }],
    issues: x.level === 'down' ? [{ kind: 'service', level: 'down', text: `${name} is not running` }] : [], open: 'ap-services',
  };
}

function monitorCard(s, t) {
  const slug = s.viewer?.slug || s.core.name;
  const m = s.monitor; const h = s.health;
  const ticks = h.days.filter((d) => d.uptime != null);
  const sparks = [];
  if (ticks.length > 1) sparks.push({ label: 'uptime · 14d', values: h.days.map((d) => (d.uptime == null ? 0 : d.uptime * 100)), color: ticks.some((d) => d.uptime < 0.99) ? 'warn' : 'ok', last: pct(last(ticks)?.uptime) });
  const p95s = h.days.map((d) => d.p95 || 0);
  if (p95s.some((v) => v > 0)) sparks.push({ label: 'p95 · daily', values: p95s, color: 'line', last: fmtMs(last(p95s)) });
  const ser = t?.series?.[slug];
  if (ser?.p95?.length) sparks.push({ label: `p95 · ${t.window?.label || 'live'}`, values: ser.p95.slice(-24), color: (last(ser.p95) || 0) > 1500 ? 'warn' : 'line', last: fmtMs(last(ser.p95)) });
  const open = s.incidents.filter((i) => i.status === 'open'); const closed = s.incidents.filter((i) => i.status !== 'open');
  const sections = [];
  if (open.length || closed.length) sections.push({ title: `incidents · ${open.length} open`, dots: [...open, ...closed].slice(0, 6).map((i) => ({ level: i.status === 'open' ? (i.kind === 'anomaly' ? 'warn' : 'down') : 'ok', text: `${i.status === 'open' ? 'open' : 'closed'} · ${i.reason || i.kind || 'probe failed'} · ${ago(i.closedAt || i.openedAt)}` })) });
  if (h.features.total) sections.push({ title: `feature checks · ${h.features.passing}/${h.features.total} passing`, dots: h.features.failing.length ? h.features.failing.map((f) => ({ level: 'warn', text: `${f} failing` })) : [{ level: 'ok', text: 'all passing' }] });
  return {
    kind: 'monitor', id: 'monitor', title: 'uptime monitor', subtitle: m.level === 'off' ? 'no probe yet' : `probe every minute · ${m.level}`, tone: toneOf(m.level),
    rows: [['last probe', m.lastCheckAt ? `${fmtMs(m.latencyMs)} · ${ago(m.lastCheckAt)}` : '—'], ['uptime 14d', pct(h.uptime14d)], ['p50 / p95', `${fmtMs(h.p50)} / ${fmtMs(h.p95)}`], ['TLS', m.tlsDaysLeft != null ? `${m.tlsDaysLeft}d left` : '—'], ['incidents open', String(open.length)]],
    sparks, sections, issues: coreIssues(s).filter((i) => ['probe', 'incident', 'tls', 'feature'].includes(i.kind)), open: 'ap-health',
  };
}

function securityCard(s) {
  const c = s.security.counts || {};
  const bars = s.security.bySeverity.filter((x) => x.count > 0).map((x) => ({ label: x.key, value: x.count }));
  return {
    kind: 'security', id: 'security', title: 'security', subtitle: s.security.scanning ? 'scan running now' : s.security.lastScanAt ? `last scan ${ago(s.security.lastScanAt)}` : 'never scanned', tone: s.security.scanning ? 'busy' : toneOf(s.security.level),
    rows: [['critical / high', `${c.critical || 0} / ${c.high || 0}`], ['medium / low', `${c.medium || 0} / ${c.low || 0}`], s.security.nextBaselineAt ? ['next baseline', new Date(s.security.nextBaselineAt).toLocaleDateString()] : null, ['assurance', s.security.assurance ? 'on' : 'off']].filter(Boolean),
    sections: bars.length ? [{ title: 'findings by severity', bars }] : [],
    issues: coreIssues(s).filter((i) => i.kind === 'scan' || i.kind === 'scanning'), open: 'ap-security',
  };
}

function twinCard(s, env) {
  const uat = env === 'uat';
  const src = uat ? s.twinSrc : s.prodSrc;
  if (uat && !s.twin.exists) {
    return { kind: 'twin', id: 'uat', title: 'UAT twin', subtitle: 'not added', tone: 'off', rows: [['status', 'no twin for this app']], issues: [], open: 'ap-envs' };
  }
  const lastDeploy = src?.lastDeploy || null;
  const list = s.deploys.filter((d) => d.env === env).slice(0, 6);
  const level = uat ? s.twin.level : s.twin.prodLevel;
  return {
    kind: 'twin', id: env, title: uat ? 'UAT twin' : 'production', subtitle: uat ? [src?.slug, src?.status].filter(Boolean).join(' · ') : (s.identity.url || '').replace(/^https?:\/\//, ''), tone: s.twin.deployRunning === env ? 'busy' : toneOf(level),
    rows: [
      uat ? ['status', src?.status || '—'] : ['status', s.identity.status || '—'],
      uat && src?.monitor ? ['probe', `${src.monitor.state || '—'}${src.monitor.lastCheckAt ? ` · ${ago(src.monitor.lastCheckAt)}` : ''}`] : null,
      ['last deploy', lastDeploy ? `${lastDeploy.status} · ${shaOf(lastDeploy.headSha || lastDeploy.mergeSha)} · ${ago(lastDeploy.at)}` : '—'],
      s.twin.deployRunning === env ? ['deploying', 'now'] : null,
      uat && src?.url ? ['url', src.url.replace(/^https?:\/\//, '')] : null,
    ].filter(Boolean),
    sections: list.length ? [{ title: `recent deploys · ${env}`, dots: list.map((d) => ({ level: d.level, text: `${d.status} · ${shaOf(d.sha)} · ${ago(d.at)}` })) }] : [],
    issues: lastDeploy?.status === 'error' ? [{ kind: 'deploy', level: 'warn', text: `last ${env} deploy failed` }] : [], open: 'ap-envs',
  };
}

function edgeCard(s, host) {
  const d = s.edge.find((x) => x.host === host);
  if (!d) return null;
  return {
    kind: 'edge', id: host, title: host, subtitle: `${d.label} domain`, tone: toneOf(d.level),
    rows: [['TLS', d.tlsDaysLeft != null ? `${d.tlsDaysLeft}d left` : 'unknown'], ['serves', d.label]],
    sections: [{ title: `edge · ${s.edge.length} domain${s.edge.length === 1 ? '' : 's'}`, dots: s.edge.map((x) => ({ level: x.level, text: `${x.host} · ${x.label}${x.tlsDaysLeft != null ? ` · TLS ${x.tlsDaysLeft}d` : ''}` })) }],
    issues: d.level !== 'ok' ? [{ kind: 'tls', level: d.level, text: `certificate expires in ${d.tlsDaysLeft}d` }] : [], open: 'ap-health',
  };
}

function planeCard(s, t) {
  const slug = s.viewer?.slug || s.core.name;
  const pc = t?.planeCalls?.[slug];
  return {
    kind: 'plane', id: 'plane', title: 'nventr control plane', subtitle: s.plane.reachable ? 'reachable · probes every minute' : 'unreachable', tone: toneOf(s.plane.level),
    rows: [['license', s.identity.license ? `${s.identity.license.status}${s.identity.license.termEnd ? ` · to ${new Date(s.identity.license.termEnd).toLocaleDateString()}` : ''}` : s.plane.license || '—'], ['kit update', s.updates.versionBehind ? `available · ${s.updates.latestKitVersion || ''}` : 'up to date'], ['pinned kit', s.identity.pinnedKitRef || '—'], s.updates.planeSha ? ['plane build', shaOf(s.updates.planeSha)] : null, pc ? ['plane calls', `${fmtN(pc.ratePerMin)}/min${pc.lastAt ? ` · ${ago(pc.lastAt)}` : ''}`] : null, ['last probe', s.monitor.lastCheckAt ? ago(s.monitor.lastCheckAt) : '—']].filter(Boolean),
    sections: s.events.length ? [{ title: 'recent activity', dots: s.events.slice(0, 5).map((e) => ({ level: e.level || 'ok', text: `${e.text} · ${ago(e.at)}` })) }] : [],
    issues: coreIssues(s).filter((i) => ['plane', 'license', 'version', 'drift'].includes(i.kind)), open: 'ap-identity',
  };
}

function costCard(s) {
  const c = s.cost;
  if (!c.available) return { kind: 'cost', id: 'cost', title: 'cost', subtitle: 'not visible at this seat', tone: 'off', rows: [], issues: [], open: 'ap-costs' };
  const parts = [['infra', c.infra?.mtdUsd ?? c.infra?.usd], ['model', c.model?.mtdUsd ?? c.model?.usd], ['dev', c.dev?.mtdUsd ?? c.dev?.usd]].filter(([, v]) => Number.isFinite(Number(v)) && Number(v) > 0).map(([k, v]) => ({ label: k, value: Number(v), text: fmtUsd(v) }));
  const users = Object.entries(c.byUser || {}).map(([k, v]) => ({ label: k, value: Number(v?.usd ?? v) || 0 })).filter((x) => x.value > 0).sort((a, b) => b.value - a.value).slice(0, 5).map((x) => ({ ...x, text: fmtUsd(x.value) }));
  return {
    kind: 'cost', id: 'cost', title: `cost · ${s.identity.period || 'this month'}`, subtitle: c.limitUsd ? `${Math.round(c.ratio * 100)}% of the $${c.limitUsd} limit` : 'no limit set · arc = share of projection', tone: toneOf(c.level),
    rows: [['month to date', fmtUsd(c.mtdUsd)], ['burn / day', fmtUsd(c.burnPerDayUsd)], ['projected', fmtUsd(c.projectedUsd)], c.limitUsd ? ['limit', fmtUsd(c.limitUsd)] : null, c.priorUsd != null ? [`prior · ${c.priorPeriod || ''}`, fmtUsd(c.priorUsd)] : null, c.pulledAt ? ['pulled', ago(c.pulledAt)] : null].filter(Boolean),
    sections: [parts.length ? { title: 'by kind', bars: parts } : null, users.length ? { title: 'by user', bars: users } : null].filter(Boolean),
    issues: coreIssues(s).filter((i) => i.kind === 'cost'), open: 'ap-costs',
  };
}

function usageCard(s, t) {
  const slug = s.viewer?.slug || s.core.name;
  const u = s.usage;
  if (!u.available) return { kind: 'usage', id: 'usage', title: 'usage', subtitle: 'no report this month', tone: 'off', rows: [], issues: [], open: 'ap-usage' };
  const ser = t?.series?.[slug];
  const sparks = [];
  if (ser?.tokens?.some((v) => v > 0)) sparks.push({ label: 'tokens', values: ser.tokens.slice(-24), color: 'ok', last: fmtN(last(ser.tokens)) });
  if (ser?.convos?.some((v) => v > 0)) sparks.push({ label: 'conversations', values: ser.convos.slice(-24), color: 'line', last: fmtN(last(ser.convos)) });
  const total = Number(u.counters.tokens) || 0;
  return {
    kind: 'usage', id: 'usage', title: `usage · ${s.identity.period || 'this month'}`, subtitle: u.reportedAt ? `reported ${ago(u.reportedAt)}` : '', tone: 'ok',
    rows: u.bars.map((b) => [b.label, fmtN(b.value)]).concat(u.statement ? [['statement', `${u.statement.lines} lines · $${(u.statement.subtotalCents / 100).toFixed(2)} · ${u.statement.status}`]] : []),
    sparks, sections: u.topUsers.length && total ? [{ title: 'top users · tokens', bars: u.topUsers.slice(0, 5).map((x) => ({ label: x.uid.slice(0, 10), value: x.tokens, text: `${fmtN(x.tokens)} · ${Math.round((x.tokens / total) * 100)}%` })) }] : [],
    issues: [], open: 'ap-usage',
  };
}

function memoryCard(s) {
  const m = s.memory;
  if (!m.available) return { kind: 'memory', id: 'memory', title: 'host memory', subtitle: 'no host sample', tone: 'off', rows: [], issues: [], open: 'ap-identity' };
  const v = m.hostVitals;
  return {
    kind: 'memory', id: 'memory', title: 'host memory', subtitle: m.hostId ? `host …${String(m.hostId).slice(-6)}` : '', tone: (m.share ?? 0) > 0.6 ? 'warn' : 'ok',
    rows: [['this stack', `${Math.round(m.memMb || 0)} MB`], ['share of sampled', m.share != null ? `${Math.round(m.share * 100)}%` : '—'], m.cpuPct != null ? ['cpu', `${Math.round(m.cpuPct)}%`] : null, v ? ['host', `${v.state || '—'}${v.memUsedPct != null ? ` · mem ${v.memUsedPct}%` : ''}${v.disk != null ? ` · disk ${v.disk}%` : ''}`] : null].filter(Boolean),
    sections: [], issues: (m.share ?? 0) > 0.6 ? [{ kind: 'memory', level: 'warn', text: 'holds most of the host’s sampled memory' }] : [], open: 'ap-identity',
  };
}

function flowCard(s, t, id) {
  const f = (t?.flows || []).find((x) => `${x.from}>${x.to}` === id);
  if (!f) return null;
  return { kind: 'flow', id, title: `${f.from} → ${f.to}`, subtitle: `${f.kind} flow · ${t.window?.label || ''}`, tone: 'ok', rows: [['rate', `${fmtN(f.ratePerMin)}/min`], ['calls', fmtN(f.n)], ['kind', f.kind]], issues: [], open: 'ap-traffic' };
}

function anomalyCard(s, t, id) {
  const a = (t?.anomalies || []).find((x) => x.id === id);
  if (!a) return null;
  const sev = a.severity === 'high' || a.severity === 'critical' ? 'down' : 'warn';
  return {
    kind: 'anomaly', id, title: `${a.metric} ${a.kind}`, subtitle: `${a.slug} · ${a.closedAt ? 'resolved' : 'open'}`, tone: a.closedAt ? 'ok' : sev,
    rows: [['value', fmtN(a.value)], ['baseline', fmtN(a.baseline)], ['z', Number(a.z).toFixed(1)], ['opened', ago(a.openedAt)], a.closedAt ? ['closed', ago(a.closedAt)] : null].filter(Boolean),
    issues: a.text ? [{ kind: 'anomaly', level: sev, text: a.text }] : [], open: 'ap-anomalies',
  };
}

// Topology-only kinds: the conduit, a work lane, the exception cluster, one
// deploy bead, a peer app, the floor timeline.
function ingressCard(s, t) {
  const g = deriveTopology(s, t); const i = g.ingress;
  return {
    kind: 'ingress', id: 'ingress', title: 'ingress', subtitle: i.reqPerMin > 0 ? `edge → app · 1 packet per ${i.density} request${i.density === 1 ? '' : 's'}` : 'edge → app · no traffic in the last bucket', tone: i.level === 'off' ? 'idle' : toneOf(i.level),
    rows: [['requests', `${fmtN(i.reqPerMin)}/min`], ['5xx', `${fmtN(i.s5xxPerMin)}/min · ${(i.errRate * 100).toFixed(1)}%`], ['4xx', `${fmtN(i.s4xxPerMin)}/min`], ['p50 / p95', `${fmtMs(i.p50)} / ${fmtMs(i.p95)}`], g.edge.uniqIps != null ? ['unique IPs', fmtN(g.edge.uniqIps)] : null, g.edge.activeUsers30m != null ? ['active users', `${fmtN(g.edge.activeUsers30m)} · 30m · ${fmtN(g.edge.activeUsersToday)} today`] : null].filter(Boolean),
    sparks: trafficSparks(t, g.slug), sections: flowSections(t, g.slug).filter((x) => x.title.startsWith('flows')),
    issues: g.anomalies.filter((a) => a.place === 'ingress').map((a) => ({ kind: 'anomaly', level: a.severity, text: a.word })), open: 'ap-traffic',
  };
}

function queueCard(s, t, id) {
  const g = deriveTopology(s, t); const l = g.work.lanes.find((x) => x.id === id);
  if (!l) return null;
  return {
    kind: 'queue', id, title: l.label, subtitle: `work lane · reported ${g.work.at ? ago(g.work.at) : '—'}`, tone: l.level === 'off' ? 'idle' : l.level === 'busy' ? 'busy' : toneOf(l.level),
    rows: [['queued', String(l.queued)], ['running', String(l.running)], ['stale claims', String(l.stale)], l.oldestQueuedMs ? ['oldest waiting', `${Math.round(l.oldestQueuedMs / 60e3)} min`] : null].filter(Boolean),
    sections: g.work.lanes.length > 1 ? [{ title: 'all lanes', dots: g.work.lanes.map((x) => ({ level: x.level === 'off' ? 'idle' : x.level, text: `${x.label} · ${x.queued} queued · ${x.running} running${x.stale ? ` · ${x.stale} stale` : ''}` })) }] : [],
    issues: [l.stale > 0 ? { kind: 'stuck', level: 'down', text: `${l.stale} claim${l.stale === 1 ? '' : 's'} stopped heart-beating` } : null, l.level === 'warn' ? { kind: 'backlog', level: 'warn', text: `oldest job has waited ${Math.round(l.oldestQueuedMs / 60e3)} min` } : null].filter(Boolean), open: 'ap-services',
  };
}

function exceptionsCard(s, t) {
  const g = deriveTopology(s, t); const e = g.app.exceptions;
  return {
    kind: 'exceptions', id: 'exceptions', title: 'exceptions', subtitle: 'unhandled errors the instance logged', tone: e.lastHour > 10 ? 'down' : e.lastHour > 0 ? 'warn' : 'ok',
    rows: [['last hour', String(e.lastHour)], ['recent', String(e.list.length)]],
    sections: e.list.length ? [{ title: 'most recent', dots: e.list.slice(0, 8).map((x) => ({ level: 'warn', text: `${x.where ? `${x.where} · ` : ''}${x.message || ''} · ${ago(x.at)}` })) }] : [],
    issues: [], open: 'ap-services',
  };
}

function deployCard(s, t, id) {
  const [env, idx] = String(id).split(':');
  const g = deriveTopology(s, t); const list = g.deploys[env] || []; const d = list[Number(idx)];
  if (!d) return null;
  return {
    kind: 'deploy', id, title: `${env} deploy · ${shaOf(d.sha)}`, subtitle: `${d.status} · ${ago(d.at)}`, tone: toneOf(d.level),
    rows: [['status', d.status], ['commit', shaOf(d.sha)], ['when', d.at ? new Date(d.at).toLocaleString() : '—']],
    sections: [{ title: `recent · ${env}`, dots: list.map((x) => ({ level: x.level, text: `${x.status} · ${shaOf(x.sha)} · ${ago(x.at)}` })) }],
    issues: d.level === 'down' ? [{ kind: 'deploy', level: 'warn', text: 'this deploy failed' }] : [], open: 'ap-envs',
  };
}

function peerCard(s, t, slug) {
  const g = deriveTopology(s, t); const p = g.peers.filter((x) => x.slug === slug);
  if (!p.length) return null;
  return {
    kind: 'peer', id: slug, title: slug, subtitle: 'peer app · live flow', tone: g.anomalies.some((a) => a.place === 'peer' && a.peer === slug) ? 'warn' : 'ok',
    rows: p.map((x) => [x.dir === 'out' ? `→ ${slug}` : `← ${slug}`, `${fmtN(x.rate)}/min · ${x.kind}`]),
    issues: g.anomalies.filter((a) => a.place === 'peer' && a.peer === slug).map((a) => ({ kind: 'anomaly', level: a.severity, text: a.word })), open: 'ap-traffic',
  };
}

function timelineCard(s, t) {
  const g = deriveTopology(s, t); const tl = g.timeline;
  const req = tl.buckets.reduce((a, b) => a + b.req, 0) / Math.max(1, tl.buckets.length);
  const e = tl.buckets.reduce((a, b) => a + b.s5xx, 0);
  return {
    kind: 'timeline', id: 'timeline', title: `timeline · ${tl.windowLabel}`, subtitle: 'traffic · deploys · incidents · uptime on one axis', tone: tl.marks.some((m) => m.open && m.level === 'down') ? 'down' : tl.marks.some((m) => m.open) ? 'warn' : 'ok',
    rows: [['avg requests', `${fmtN(req)}/min`], ['5xx in window', fmtN(e)], ['deploys', String(tl.deploys.length)], ['incidents / anomalies', String(tl.marks.length)], ['uptime 14d', pct(tl.uptime14d)]],
    sparks: trafficSparks(t, g.slug),
    sections: tl.marks.length ? [{ title: 'marks', dots: tl.marks.slice(-8).reverse().map((m) => ({ level: m.level, text: `${m.kind} · ${ago(m.at)}${m.open ? ' · open' : ''}` })) }] : [],
    issues: [], open: 'ap-traffic',
  };
}

export function commandInspector(s0, t, sel) {
  if (!s0) return null;
  const s = simOverlay(s0, t);
  if (!sel || sel.kind === 'core' || sel.kind === 'rack') return coreCard(s, t);
  if (sel.kind === 'ingress') return ingressCard(s, t);
  if (sel.kind === 'queue') return queueCard(s, t, sel.id);
  if (sel.kind === 'exceptions') return exceptionsCard(s, t);
  if (sel.kind === 'deploy') return deployCard(s, t, sel.id);
  if (sel.kind === 'peer') return peerCard(s, t, sel.id);
  if (sel.kind === 'timeline') return timelineCard(s, t);
  if (sel.kind === 'service') return serviceCard(s, sel.id);
  if (sel.kind === 'monitor') return monitorCard(s, t);
  if (sel.kind === 'security') return securityCard(s);
  if (sel.kind === 'twin') return twinCard(s, sel.id);
  if (sel.kind === 'edge') return edgeCard(s, sel.id);
  if (sel.kind === 'plane') return planeCard(s, t);
  if (sel.kind === 'cost') return costCard(s);
  if (sel.kind === 'usage') return usageCard(s, t);
  if (sel.kind === 'memory') return memoryCard(s);
  if (sel.kind === 'flow') return flowCard(s, t, sel.id);
  if (sel.kind === 'anomaly') return anomalyCard(s, t, sel.id);
  return null;
}
